import { FormConditions } from '../../components/forms/question/utils';
import { gracefullyParseJson } from '../../utils';
import {
  AnswerValue,
  ContactValue,
  GroupedFormData,
  GroupedResponse,
  MedicationValue,
  PrimaryCarePhysicianValue,
  ProfessionalResourceValue,
  ProviderFormElement,
  QuestionOption,
  QuestionTypeEnum,
} from './types';

export const INTAKE_ANSWER_TEXT_CLASSNAME = 'rebrand:text-text-inverse';

export const INTAKE_ANSWER_TEXT_CLASSNAME_COMPACT = 'text-small mb-2 rebrand:text-text-inverse';

export const INTAKE_MASKED_ANSWER_TEXT_CLASSNAME = `fs-exclude ${INTAKE_ANSWER_TEXT_CLASSNAME}`;

export const INTAKE_UNANSWERED_FALLBACK = 'No response';

export type IntakeFormValues = Record<string, AnswerValue>;

const READ_ONLY_SHOW_UNCHECKED_GROUPS = new Set(['health_history_checklist']);

type ReadOnlyAnswerKind = 'inline' | 'text' | 'group' | 'unhandled';

export function getReadOnlyAnswerKind(type?: QuestionTypeEnum): ReadOnlyAnswerKind {
  switch (type) {
    case QuestionTypeEnum.CHECKBOX:
    case QuestionTypeEnum.MULTI_SELECT_PILL:
    case QuestionTypeEnum.RADIO:
    case QuestionTypeEnum.SELECT:
    case QuestionTypeEnum.LIKERT_SCALE:
      return 'inline';
    case QuestionTypeEnum.TEXT:
    case QuestionTypeEnum.TEXT_AREA:
    case QuestionTypeEnum.TEXT_WITH_SUGGESTIONS:
    case QuestionTypeEnum.CHOSEN_NAME_INPUT:
    case QuestionTypeEnum.PHONE_NUMBER_INPUT:
      return 'text';
    case QuestionTypeEnum.CONDITIONAL_CHECKBOX_GROUP:
      return 'group';
    default:
      return 'unhandled';
  }
}

export function buildIntakeFormValues(selectedForm: GroupedFormData): IntakeFormValues {
  const values: IntakeFormValues = {};

  for (const questions of Object.values(selectedForm.formQuestionsWithAnswers)) {
    for (const question of questions) {
      if (question.questionName && question.answer !== undefined) {
        values[question.questionName] = question.answer;
      }
    }
  }

  for (const page of selectedForm.formAnswers ?? []) {
    for (const element of page.elements) {
      if (values[element.name] !== undefined) {
        continue;
      }

      values[element.name] = parseStoredAnswerValue(element.value);
    }
  }

  return values;
}

export function toGroupedResponse(element: ProviderFormElement, formValues: IntakeFormValues): GroupedResponse {
  return {
    type: element.type,
    answer: formValues[element.name],
    question: element.title,
    questionName: element.name,
    questionElement: element,
  };
}

export function isIntakeElementDisplayed(
  element: Pick<ProviderFormElement, 'visibleIf'> | undefined,
  values: IntakeFormValues,
): boolean {
  const conditionName = element?.visibleIf;
  if (!conditionName) {
    return true;
  }

  const condition = FormConditions[conditionName];
  return !condition || condition(values);
}

function getStringValue(value: AnswerValue | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isEmptyStringArray(answer: string[]): boolean {
  return answer.every((entry) => typeof entry !== 'string' || entry.trim() === '');
}

function isEmptyMedicationArray(answer: MedicationValue[]): boolean {
  return answer.every((medication) => !medication.medicationName?.trim() && !medication.dosage?.trim());
}

export function getSelectedValues(value: AnswerValue | undefined): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  const singleValue = getStringValue(value);
  return singleValue ? [singleValue] : [];
}

export function formatSelectedOptionLabels(options: QuestionOption[] | undefined, selectedValues: string[]): string {
  if (!options?.length || selectedValues.length === 0) {
    return '';
  }

  const labelsByValue = new Map(options.map((option) => [option.value, option.label]));
  return selectedValues.map((selectedValue) => labelsByValue.get(selectedValue) ?? selectedValue).join(', ');
}

export function getInlineDisplayValue(
  element: Pick<ProviderFormElement, 'options' | 'type'>,
  values: IntakeFormValues,
  fieldName: string,
): string {
  const fieldValue = values[fieldName];

  if (getReadOnlyAnswerKind(element.type) === 'text') {
    return getStringValue(fieldValue);
  }

  return formatSelectedOptionLabels(element.options, getSelectedValues(fieldValue));
}

export function formatOptionAsQuestion(optionLabel: string): string {
  return optionLabel.endsWith('?') ? optionLabel : `${optionLabel}?`;
}

export type IntakeFollowUpPresentation =
  | { kind: 'none' }
  | { kind: 'labeled-row'; label: string; value: string }
  | { kind: 'value-row'; value: string }
  | { kind: 'nested-group'; element: ProviderFormElement }
  | { kind: 'complex'; groupedResponse: GroupedResponse; label?: string };

export function getOptionalUnansweredFollowUpPresentation(element: ProviderFormElement): IntakeFollowUpPresentation {
  if (getReadOnlyAnswerKind(element.type) === 'text' && !element.title) {
    return { kind: 'value-row', value: INTAKE_UNANSWERED_FALLBACK };
  }

  return { kind: 'labeled-row', label: element.title ?? '', value: INTAKE_UNANSWERED_FALLBACK };
}

function getInlineTextFollowUpPresentation(
  element: ProviderFormElement,
  formValues: IntakeFormValues,
): IntakeFollowUpPresentation {
  const answerKind = getReadOnlyAnswerKind(element.type);
  const displayValue = getInlineDisplayValue(element, formValues, element.name);

  if (!displayValue) {
    if (!isOptionalIntakeQuestion(element)) {
      return { kind: 'none' };
    }

    return getOptionalUnansweredFollowUpPresentation(element);
  }

  if (answerKind === 'text' && !element.title) {
    return { kind: 'value-row', value: displayValue };
  }

  return { kind: 'labeled-row', label: element.title ?? '', value: displayValue };
}

export function getFollowUpPresentation(
  element: ProviderFormElement,
  formValues: IntakeFormValues,
): IntakeFollowUpPresentation {
  if (!isIntakeElementDisplayed(element, formValues)) {
    return { kind: 'none' };
  }

  const answerKind = getReadOnlyAnswerKind(element.type);

  if (answerKind === 'inline' || answerKind === 'text') {
    return getInlineTextFollowUpPresentation(element, formValues);
  }

  if (answerKind === 'group') {
    return { kind: 'nested-group', element };
  }

  const groupedResponse = toGroupedResponse(element, formValues);

  if (isOptionalUnansweredIntakeQuestion(groupedResponse)) {
    return getOptionalUnansweredFollowUpPresentation(element);
  }

  return { kind: 'complex', groupedResponse, label: element.title };
}

export function getConditionalCheckboxOptionFollowUps(
  option: NonNullable<ProviderFormElement['options']>[number],
  formValues: IntakeFormValues,
): {
  displayValue: string;
  followUps: Array<{ key: string; presentation: IntakeFollowUpPresentation }>;
} {
  const inlineValues = ['Yes'];
  const followUps: Array<{ key: string; presentation: IntakeFollowUpPresentation }> = [];

  for (const followUpElement of option.followUpElements ?? []) {
    if (!isIntakeElementDisplayed(followUpElement, formValues)) {
      continue;
    }

    if (getReadOnlyAnswerKind(followUpElement.type) === 'inline') {
      const inlineValue = getInlineDisplayValue(followUpElement, formValues, followUpElement.name);
      if (inlineValue) {
        inlineValues.push(inlineValue);
      } else if (isOptionalIntakeQuestion(followUpElement) && followUpElement.title) {
        followUps.push({
          key: followUpElement.name,
          presentation: getOptionalUnansweredFollowUpPresentation(followUpElement),
        });
      }
      continue;
    }

    const presentation = getFollowUpPresentation(followUpElement, formValues);
    if (presentation.kind !== 'none') {
      followUps.push({ key: followUpElement.name, presentation });
    }
  }

  return {
    displayValue: inlineValues.join(', '),
    followUps,
  };
}

export function shouldShowUncheckedOptionsInReadOnly(groupName: string | undefined): boolean {
  return !!groupName && READ_ONLY_SHOW_UNCHECKED_GROUPS.has(groupName);
}

export function parseStoredAnswerValue(rawValue: string): AnswerValue {
  return gracefullyParseJson<AnswerValue>(rawValue) ?? rawValue;
}

export function formatPrimaryCarePhysician(pcp: PrimaryCarePhysicianValue | undefined): string {
  const fullName = [pcp?.client_pcp_first_name, pcp?.client_pcp_last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return [fullName, pcp?.client_pcp_address?.trim()].filter(Boolean).join(', ');
}

function isDisplayOnlyIntakeQuestion(type?: QuestionTypeEnum): boolean {
  return type === QuestionTypeEnum.DIVIDER || type === QuestionTypeEnum.SECTION_TITLE;
}

export function isOptionalIntakeQuestion(question: {
  questionElement?: Pick<ProviderFormElement, 'required'>;
  required?: boolean;
}): boolean {
  const required = question.questionElement?.required ?? question.required;
  return required === false;
}

export function isEmptyIntakeAnswer(answer: AnswerValue | undefined, type?: QuestionTypeEnum): boolean {
  if (answer === undefined || answer === null || answer === 'null') {
    return true;
  }

  if (typeof answer === 'string') {
    return answer.trim() === '';
  }

  if (Array.isArray(answer)) {
    if (answer.length === 0) {
      return true;
    }

    if (type === QuestionTypeEnum.EMERGENCY_CONTACT_INPUT) {
      return (answer as ContactValue[]).every((contact) => !contact.name?.trim() && !contact.phoneNumber?.trim());
    }

    if (type === QuestionTypeEnum.MEDICATION_INPUT || type === QuestionTypeEnum.MEDICATION_INPUT_WITH_SUGGESTIONS) {
      return isEmptyMedicationArray(answer as MedicationValue[]);
    }

    if (answer.every((entry) => typeof entry === 'string')) {
      return isEmptyStringArray(answer as string[]);
    }

    return false;
  }

  if (type === QuestionTypeEnum.PRIMARY_CARE_PHYSICIAN_INPUT) {
    return !formatPrimaryCarePhysician(answer as PrimaryCarePhysicianValue);
  }

  if (type === QuestionTypeEnum.PROFESSIONAL_RESOURCE_INPUT) {
    const resource = answer as ProfessionalResourceValue;
    const hasAddressOrPhone = !!resource.address?.trim() || !!resource.phoneNumber?.trim();
    const hasResources = resource.resources?.some((entry) => !!entry.name?.trim() || !!entry.phoneNumber?.trim());

    return !hasAddressOrPhone && !hasResources;
  }

  return false;
}

export function shouldRenderIntakeQuestion(question: GroupedResponse): boolean {
  if (isDisplayOnlyIntakeQuestion(question.type)) {
    return !!question.answer;
  }

  if (!isEmptyIntakeAnswer(question.answer, question.type)) {
    return true;
  }

  return isOptionalIntakeQuestion(question);
}

export function isOptionalUnansweredIntakeQuestion(question: GroupedResponse): boolean {
  return isOptionalIntakeQuestion(question) && isEmptyIntakeAnswer(question.answer, question.type);
}
